<!-- eslint-disable vue/v-slot-style -->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useIntegrationHook } from 'dashboard/composables/useIntegrationHook';
import { FormKit } from '@formkit/vue';

import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    FormKit,
    NextButton,
  },
  props: {
    integrationId: {
      type: String,
      required: true,
    },
  },
  emits: ['close'],
  setup(props) {
    const { integration, isHookTypeInbox } = useIntegrationHook(
      props.integrationId
    );

    return { integration, isHookTypeInbox };
  },
  data() {
    return {
      endPoint: '',
      alertMessage: '',
      values: {},
      isTestingConnection: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'integrations/getUIFlags',
      activeInboxes: 'inboxes/getActiveInboxesWithInternal',
      dialogFlowEnabledInboxes: 'inboxes/dialogFlowEnabledInboxes',
    }),
    inboxes() {
      if (this.isIntegrationDialogflow) {
        return this.dialogFlowEnabledInboxes
          .filter(inbox => !this.connectedDialogflowInboxIds.includes(inbox.id))
          .map(inbox => ({
            label: inbox.internal_name ?? inbox.name,
            value: inbox.id,
          }));
      }
      return this.activeInboxes.map(inbox => ({
        label: inbox.name,
        value: inbox.id,
      }));
    },
    connectedDialogflowInboxIds() {
      if (!this.isIntegrationDialogflow) {
        return [];
      }
      return this.integration.hooks.map(hook => hook.inbox?.id);
    },
    formItems() {
      return this.integration.settings_form_schema;
    },
    visibleFormItems() {
      return this.formItems.filter(item => this.matchesConditions(item));
    },
    isIntegrationDialogflow() {
      return this.integration.id === 'dialogflow';
    },
    showTestButton() {
      return !!this.integration.enable_test_connection;
    },
  },
  methods: {
    // A schema item can declare `conditions: { field: expectedValue }` to be
    // shown only when the current form values match (all conditions, AND).
    matchesConditions(item) {
      if (!item.conditions) return true;
      return Object.entries(item.conditions).every(
        ([field, expected]) => this.values[field] === expected
      );
    },
    // `conditions` is a Chatwoot-only hint, strip it before binding to FormKit.
    fieldProps(item) {
      const { conditions, ...rest } = item;
      return rest;
    },
    onClose() {
      this.$emit('close');
    },
    async testConnection() {
      this.isTestingConnection = true;
      try {
        const result = await this.$store.dispatch(
          'integrations/testHookConnection',
          { integrationId: this.integration.id, settings: this.values }
        );
        useAlert(
          result.success
            ? this.$t('INTEGRATION_APPS.TEST_CONNECTION.SUCCESS')
            : result.error || this.$t('INTEGRATION_APPS.TEST_CONNECTION.ERROR')
        );
      } catch (error) {
        useAlert(this.$t('INTEGRATION_APPS.TEST_CONNECTION.ERROR'));
      } finally {
        this.isTestingConnection = false;
      }
    },
    buildHookPayload() {
      const hookPayload = {
        app_id: this.integration.id,
        settings: {},
      };

      hookPayload.settings = Object.keys(this.values).reduce((acc, key) => {
        if (key !== 'inbox') {
          acc[key] = this.values[key];
        }
        return acc;
      }, {});

      this.formItems.forEach(item => {
        if (item.validation.includes('JSON')) {
          hookPayload.settings[item.name] = JSON.parse(
            hookPayload.settings[item.name]
          );
        }
      });

      if (this.isHookTypeInbox && this.values.inbox) {
        hookPayload.inbox_id = this.values.inbox;
      }

      return hookPayload;
    },
    async submitForm() {
      try {
        await this.$store.dispatch(
          'integrations/createHook',
          this.buildHookPayload()
        );
        this.alertMessage = this.$t('INTEGRATION_APPS.ADD.API.SUCCESS_MESSAGE');
        this.onClose();
      } catch (error) {
        const errorMessage = error?.response?.data?.message;
        this.alertMessage =
          errorMessage || this.$t('INTEGRATION_APPS.ADD.API.ERROR_MESSAGE');
      } finally {
        useAlert(this.alertMessage);
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto integration-hooks">
    <woot-modal-header
      :header-title="integration.name"
      :header-content="integration.description"
    />
    <FormKit
      v-model="values"
      type="form"
      form-class="w-full grid gap-4"
      :submit-attrs="{
        inputClass: 'hidden',
        wrapperClass: 'hidden',
      }"
      :incomplete-message="false"
      @submit="submitForm"
    >
      <FormKit
        v-for="item in visibleFormItems"
        :key="item.name"
        v-bind="fieldProps(item)"
      />
      <FormKit
        v-if="isHookTypeInbox"
        :options="inboxes"
        type="select"
        name="inbox"
        input-class="reset-base"
        :placeholder="$t('INTEGRATION_APPS.ADD.FORM.INBOX.LABEL')"
        :label="$t('INTEGRATION_APPS.ADD.FORM.INBOX.PLACEHOLDER')"
        validation="required"
        validation-name="Inbox"
      />
      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <NextButton
          v-if="showTestButton"
          faded
          blue
          type="button"
          class="ltr:mr-auto rtl:ml-auto"
          :label="$t('INTEGRATION_APPS.TEST_CONNECTION.BUTTON')"
          :is-loading="isTestingConnection"
          @click.prevent="testConnection"
        />
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('INTEGRATION_APPS.ADD.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :label="$t('INTEGRATION_APPS.ADD.FORM.SUBMIT')"
          :is-loading="uiFlags.isCreatingHook"
        />
      </div>
    </FormKit>
  </div>
</template>

<style lang="css">
.formkit-outer {
  @apply mt-2;
}

.formkit-form > .formkit-wrapper > ul.formkit-messages {
  @apply hidden;
}

/* equivalent of .reset-base */
.formkit-input {
  margin-bottom: 0px !important;
}

[data-invalid] .formkit-message {
  @apply text-red-500 block text-xs font-normal my-1 w-full;
}

.formkit-outer[data-type='checkbox'] .formkit-wrapper {
  @apply flex items-center gap-2 px-0.5;
}

.formkit-messages {
  @apply list-none m-0 p-0;
}

.formkit-actions {
  @apply hidden;
}
</style>
